import { CodeTaskInput } from '@automata/wfb-api';
import reduce from 'lodash/reduce';
import uniq from 'lodash/uniq';

import { CodeTaskProperties } from '../../../types';
import { DataDependenciesByTaskId, InputValue } from '../../shared/types';
import addDefaultArgumentValues from '../addDefaultArgumentValues';
import { isConnectorRef } from '../utils';

type GetCodeTaskProps = {
  taskId: string;
  taskProperties: CodeTaskProperties;
  taskDependencies: string[];
  dataDependenciesByTaskId: DataDependenciesByTaskId;
};

export function getCodeTask({
  taskId,
  taskProperties,
  taskDependencies,
  dataDependenciesByTaskId,
}: GetCodeTaskProps): CodeTaskInput & { type: 'code' } {
  const dataDependencies = dataDependenciesByTaskId[taskId];

  const dependencies = uniq([
    ...taskDependencies,
    ...Object.values(dataDependencies ?? {})
      .filter((x): x is { task_id: string } => 'task_id' in x)
      .map((x) => x.task_id),
  ]);

  const taskParametersWithDefaultValues = addDefaultArgumentValues(
    taskProperties.taskType,
    taskProperties.taskParameters ?? null,
    taskProperties.taskAppliedParameters
  );

  const taskParametersInputs: CodeTaskInput['inputs'] = reduce(
    taskParametersWithDefaultValues,
    (acc, parameterValue, key) => {
      const parameterId = taskProperties.taskAppliedParameters?.[key];
      let input: InputValue | null = null;

      if (parameterId) {
        input = { parameter_id: parameterId };
      } else if (isConnectorRef(parameterValue)) {
        input = parameterValue;
      } else if (parameterValue) {
        input = { value: parameterValue };
      }

      return input ? { ...acc, [key]: input } : acc;
    },
    {}
  );

  return {
    id: taskId,
    description: taskProperties.nickname,
    type: 'code',
    dependencies,
    function: {
      type: 'python_reference',
      value: taskProperties.scriptName,
    },
    inputs: {
      ...dataDependenciesByTaskId[taskId],
      ...taskParametersInputs,
    },
  };
}
